import { CommonModule } from '@angular/common';
import { Component, computed, ElementRef, inject, input, output, signal, viewChild } from '@angular/core';
import { UserRole } from '@lb/api/mylb/enums/role.enum';
import { UserV2Service } from '@lb/api/mylb/services/user-v2.service';
import { IUserListItem } from '@lb/api/mylb/interfaces';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { debounceTime, filter, switchMap, tap } from 'rxjs';

@Component({
	selector: 'lb-user-search',
	templateUrl: './user-search.component.html',
	styleUrls: ['./user-search.component.scss'],
	standalone: true,
	imports: [CommonModule],
	host: { '[class.full-width]': 'fullWidth()' }
})
export class UserSearchComponent {
	private readonly userService = inject(UserV2Service);
	private readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');

	readonly placeholder = input<string>('Search for users');
	readonly roles = input<UserRole[]>();
	/** Stretch the search input to its container's width instead of the fixed default. */
	readonly fullWidth = input<boolean>(false);
	readonly multiSelect = input<boolean>(false);
	readonly persistSelection = input<boolean>(false);
	readonly resultSize = input<number>(15);
	readonly showSelected = input<boolean>(false);
	readonly userSelected = output<{ user: IUserListItem; isChecked: boolean }>();

	readonly searchTerm = signal<string>('');
	readonly inputFocused = signal<boolean>(false);

	searchedUsers = signal<IUserListItem[]>([]);
	selectedUsers = signal<IUserListItem[]>([]);
	loading = signal<boolean>(false);

	showResults = computed(() => this.inputFocused() && (this.searchedUsers().length > 0 || this.loading()));

	constructor() {
		toObservable(this.searchTerm)
			.pipe(
				takeUntilDestroyed(),
				debounceTime(300),
				tap((term) => {
					if (term.trim().length < 3) {
						this.searchedUsers.set([]);
						this.loading.set(false);
					}
				}),
				filter((term) => term.trim().length > 1),
				tap(() => this.loading.set(true)),
				switchMap((term) =>
					this.userService.searchUsers({
						searchTerm: term,
						roles: this.roles() ?? [],
						status: 3,
						page: 1,
						pageSize: this.resultSize()
					})
				)
			)
			.subscribe((users) => {
				this.searchedUsers.set(users.items);
				this.loading.set(false);
			});
	}

	onSearchTermChange(value: string): void {
		this.searchTerm.set(value);
	}

	onInputFocus(): void {
		this.inputFocused.set(true);
	}

	onInputBlur(): void {
		// Delay to allow mousedown on results to fire before hiding
		setTimeout(() => {
			this.inputFocused.set(false);
			this.searchTerm.set('');
			this.searchedUsers.set([]);
			if (!this.persistSelection()) {
				this.selectedUsers.set([]);
			}
		}, 200);
	}

	selectUser(user: IUserListItem): void {
		if (this.isSelected(user.userID)) {
			this.selectedUsers.update((users) => users.filter((u) => u.userID !== user.userID));
			this.userSelected.emit({ user, isChecked: false });
		} else {
			if (this.multiSelect()) {
				this.selectedUsers.update((users) => [...users, user]);
			} else {
				this.selectedUsers.set([user]);
			}
			this.userSelected.emit({ user, isChecked: true });
		}

		// Keep popup open by re-focusing the input
		this.searchInput()?.nativeElement.focus();
	}

	removeUser(user: IUserListItem): void {
		this.selectedUsers.update((users) => users.filter((u) => u.userID !== user.userID));
		this.userSelected.emit({ user, isChecked: false });
	}

	isSelected(userID: number): boolean {
		return this.selectedUsers().some((u) => u.userID === userID);
	}
}
